import { cx } from '@linaria/core';
import { useId, useRef } from 'react';
import { pm2ConfigStore } from '@pm2/store';
import { avoidBreakInside } from '@provider/print';
import { resets } from '@ui/css';
import { speechButtonSelectors } from '@ui/kit/selectors';
import { Chevron, CollapsibleTransition } from '@utils/react';
import { headerFocusContainer, header, headingCls, chevronCls, toggleBtnCls, contentCls, accordionCollapseDurationMs, contentPaddingCls, wrapperCls, expandedModifierCls } from './accordion-panel.style.js';
import { jsxs, jsx } from 'react/jsx-runtime';

function AccordionPanel(props) {
  const audience = pm2ConfigStore.useSelector(state => state.config.theme.targetAudience);
  const accordionId = useId();
  const togglerRef = useRef(null);
  const HeadingTag = props.titleTag;
  const toggleAccordion = () => {
    props.onToggle(props.id);
  };

  /** allow navigating between accordions using arrow keys */
  const handleKeyPress = evt => {
    const collection = props.collectionRef.current;
    const toggler = togglerRef.current;
    if (!collection || !toggler) {
      return;
    }

    // find list of accordion DOM nodes from the collection
    const allTogglers = Array.from(collection.children).map(it => it.querySelector(`[data-selector="accordion-toggler"]`)).filter(it => it !== null);
    const indexOfToggler = allTogglers.indexOf(toggler);

    // handle navigational shortcuts
    let hasHandledEvent = false;
    switch (evt.key) {
      case "ArrowUp":
        {
          const nextIndex = indexOfToggler === 0 ? allTogglers.length - 1 : indexOfToggler - 1;
          allTogglers[Number(nextIndex)]?.focus();
          hasHandledEvent = true;
          break;
        }
      case "ArrowDown":
        {
          const nextIndex = indexOfToggler === allTogglers.length - 1 ? 0 : indexOfToggler + 1;
          allTogglers[Number(nextIndex)]?.focus();
          hasHandledEvent = true;
          break;
        }
      case "Home":
        allTogglers[0]?.focus();
        hasHandledEvent = true;
        break;
      case "End":
        allTogglers[allTogglers.length - 1]?.focus();
        hasHandledEvent = true;
        break;
    }
    if (hasHandledEvent) {
      evt.preventDefault();
      evt.stopPropagation();
    }
  };
  return /*#__PURE__*/jsxs("section", {
    className: cx(speechButtonSelectors.accordionPanel.className, wrapperCls, resets.rootCls, `heading--${props.titleTag}`, audience === "Grade1To3" && "audience--grade1To3", audience === "Grade4To6" && "audience--grade4To6", props.isExpanded && expandedModifierCls, props.isExpanded && speechButtonSelectors.accordionPanelExpandedModifer.className),
    children: [/*#__PURE__*/jsx("div", {
      className: headerFocusContainer,
      ...avoidBreakInside(),
      children: /*#__PURE__*/jsx("header", {
        className: header,
        "data-content-component": true,
        children: /*#__PURE__*/jsxs("button", {
          ref: togglerRef,
          id: `${accordionId}-toggler`,
          className: cx(speechButtonSelectors.accordionToggleButton.className, toggleBtnCls),
          onClick: toggleAccordion,
          onKeyDown: handleKeyPress,
          "data-selector": "accordion-toggler",
          "aria-expanded": props.isExpanded,
          "aria-controls": `${accordionId}-region`,
          children: [/*#__PURE__*/jsx(HeadingTag, {
            className: headingCls,
            children: props.title
          }), /*#__PURE__*/jsx("span", {
            className: chevronCls,
            children: /*#__PURE__*/jsx(Chevron, {
              size: 9,
              lineWidth: 1.5,
              vertical: true,
              inversed: props.isExpanded
            })
          })]
        })
      })
    }), /*#__PURE__*/jsx("div", {
      role: "region",
      "aria-labelledby": `${accordionId}-toggler`,
      children: /*#__PURE__*/jsx(CollapsibleTransition, {
        axis: "vertical",
        in: props.isExpanded,
        timeout: accordionCollapseDurationMs,
        classNames: contentCls,
        retainElementPositionDuringAnimation: props.canBeAutoCollapsed ? {
          target: "window"
        } : undefined,
        delayMs: 10,
        mountOnEnter: true,
        unmountOnExit: true,
        children: ref => /*#__PURE__*/jsx("div", {
          ref: ref,
          className: contentCls,
          children: /*#__PURE__*/jsx("div", {
            "data-accordion-panel": true,
            className: cx(contentPaddingCls, props.isLast && "last"),
            children: props.children
          })
        })
      })
    })]
  });
}

export { AccordionPanel };
//# sourceMappingURL=accordion-panel.js.map
